import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Play, Pause, Microphone, MusicNotes } from "@phosphor-icons/react";
import api, { fileUrl } from "../lib/api";

const MEDALS = ["🥇", "🥈", "🥉"];

/** RemixSoundCharts — weekly ranking of remix sounds, crediting the original voice. */
export default function RemixSoundCharts() {
  const [items, setItems] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/music/remix-charts", { params: { limit: 10 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
    return () => { if (audioRef.current) audioRef.current.pause(); };
  }, []);

  const toggle = (e, s) => {
    e.preventDefault();
    e.stopPropagation();
    if (playingId === s.id) { audioRef.current?.pause(); setPlayingId(null); return; }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(fileUrl(s.storage_path) || s.audio_url);
    a.onended = () => setPlayingId(null);
    a.play().catch(() => {});
    audioRef.current = a;
    setPlayingId(s.id);
  };

  if (items === null) return <p className="mt-6 text-cream/40 text-sm">Loading…</p>;

  if (items.length === 0) {
    return (
      <div className="mt-6 crystal-glass rounded-3xl p-6" data-testid="remix-charts-empty">
        <p className="text-cream font-bold">No remix sounds yet.</p>
        <p className="text-cream/60 text-sm mt-1">Tap <span className="text-sky-300 font-black">Remix</span> on any voice reaction to turn it into a sound — the most-used ones chart here every week.</p>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-2" data-testid="remix-charts">
      {items.map((s) => (
        <div
          key={s.id}
          onClick={() => nav(`/create?music=${s.id}`)}
          data-testid={`remix-row-${s.id}`}
          className={`flex items-center gap-3 p-3.5 rounded-2xl border cursor-pointer transition-colors ${
            s.rank <= 3 ? "bg-sky-400/10 border-sky-400/30 hover:border-sky-400/60" : "bg-white/5 border-white/5 hover:border-papaya/40"
          }`}
        >
          <div className="w-8 text-center text-cream/60 font-black text-sm">{MEDALS[s.rank - 1] || `#${s.rank}`}</div>
          <button
            onClick={(e) => toggle(e, s)}
            data-testid={`remix-play-${s.id}`}
            className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
              playingId === s.id ? "bg-sky-400 text-ink animate-pulse" : "bg-sky-400/20 text-sky-300"
            }`}
          >
            {playingId === s.id ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-cream font-black truncate text-sm">{s.title}</p>
            {s.original_voice ? (
              <Link
                to={`/u/${s.original_voice.username}`}
                onClick={(e) => e.stopPropagation()}
                data-testid={`remix-voice-${s.id}`}
                className="inline-flex items-center gap-1 text-jam-gold text-[11px] font-bold hover:underline"
              >
                <Microphone size={11} weight="fill" /> Original voice · @{s.original_voice.username}
              </Link>
            ) : (
              <p className="text-cream/50 text-[11px]">{s.artist}</p>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-sky-300 text-sm font-black tabular-nums flex items-center justify-end gap-1" data-testid={`remix-uses-${s.id}`}>
              <MusicNotes size={12} weight="fill" /> {s.weekly_uses}
            </p>
            <p className="text-cream/40 text-[9px] tracking-[0.15em] uppercase font-bold">this week</p>
          </div>
        </div>
      ))}
    </div>
  );
}
